import { MoreVert, Save } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import dayjs, { type Dayjs } from 'dayjs';
import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router';
import Icon from '/@/components/icon';
import useUnitProvider from '/@/core/context/unit';
import { useGetUserNode } from '/@/hooks/nodes';
import { useCreateOrUpdateCropMonitoringResult } from '/@/hooks/reports';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { CropMonitoringNote } from '/@/types/crop-monitoring-note';
import { formatDate } from '/@/utils';
import { reverse } from '/@/utils/router';
import { getUserDisplayName, RegistryFooter } from './card-footer';
import { EditActions } from './edit-actions';
import { RegistryCard } from './registry-card';
import RegistryNotes from './registry-notes';

const Cell = ({
    title,
    value,
    highlighted = false,
}: {
    title: string;
    value: string | number;
    highlighted?: boolean;
}) => (
    <Stack
        direction='column'
        spacing={0.5}
        sx={{ p: 1, backgroundColor: highlighted ? 'primary.mui.stroke' : 'grey.100' }}
    >
        <Typography variant='caption' color='primary'>
            {title}
        </Typography>
        <Typography variant='subtitle1' color='primary' fontWeight='bold'>
            {value}
        </Typography>
    </Stack>
);

const EditCell = ({ children }: { children: ReactNode }) => (
    <Box
        sx={{
            p: 1,
            backgroundColor: 'grey.100',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 1,
        }}
    >
        {children}
    </Box>
);

const RegistryHarvest = ({
    cropMonitoring,
    notes,
}: {
    cropMonitoring: CropMonitoring;
    notes: CropMonitoringNote[];
}) => {
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [editing, setEditing] = useState(false);
    const navigate = useNavigate();
    const units = useUnitProvider();

    const fromFieldview = !!cropMonitoring.result?.harvestReport;
    // Once the campaign is closed, the harvest result is locked — same as every other
    // registry card, editing only applies while the crop monitoring is still active.
    const canEdit = cropMonitoring.active;
    const closeMenu = () => setMenuAnchor(null);

    const [harvestDate, setHarvestDate] = useState<Dayjs | null>(
        cropMonitoring.harvestDate ? dayjs(cropMonitoring.harvestDate) : null
    );
    const [humidity, setHumidity] = useState<number | undefined>(cropMonitoring.result?.humidityAtHarvest);
    const [averageYield, setAverageYield] = useState<number | undefined>(cropMonitoring.result?.averageYield);

    const startEditing = () => {
        closeMenu();
        setHarvestDate(cropMonitoring.harvestDate ? dayjs(cropMonitoring.harvestDate) : null);
        setHumidity(cropMonitoring.result?.humidityAtHarvest);
        setAverageYield(cropMonitoring.result?.averageYield);
        setEditing(true);
    };

    const cancelEditing = () => setEditing(false);

    const { trigger: saveResult, isMutating } = useCreateOrUpdateCropMonitoringResult({
        cropMonitoringId: cropMonitoring.id,
        fieldId: cropMonitoring.field,
    });

    const isValid = Boolean(harvestDate);

    const handleSave = async () => {
        if (!isValid || !harvestDate) return;
        await saveResult({
            // Sent unchanged alongside the edited fields — this is a PUT, so omitting them
            // would risk clobbering the existing planting/harvest report links.
            plantingDate: cropMonitoring.plantingDate,
            plantingReport: cropMonitoring.result?.plantingReport ?? undefined,
            harvestReport: cropMonitoring.result?.harvestReport ?? undefined,
            harvestDate: harvestDate.format('YYYY-MM-DD'),
            humidityAtHarvest: humidity,
            averageYield: averageYield,
        });
        setEditing(false);
    };

    const handleViewReport = () => {
        navigate(
            reverse('cropMonitoring.CornCropMonitoringDetail.report', {
                params: { cropMonitoringId: cropMonitoring.id },
            })
        );
    };

    const harvestDateDisplay = cropMonitoring.harvestDate ? formatDate(new Date(cropMonitoring.harvestDate)) : '—';
    const harvestedArea =
        cropMonitoring.result?.totalHarvestedArea != null
            ? units.toArea(cropMonitoring.result.totalHarvestedArea, 2).toString()
            : '—';
    const humidityDisplay =
        cropMonitoring.result?.humidityAtHarvest != null
            ? `${cropMonitoring.result.humidityAtHarvest.toFixed(2)}%`
            : '—';
    const averageYieldDisplay =
        cropMonitoring.result?.averageYield != null
            ? units.toYield(cropMonitoring.result.averageYield, cropMonitoring.crop, 2).toString()
            : '—';

    const userNodeQuery = useGetUserNode(cropMonitoring.result?.createdBy ?? 0);
    const authorName = getUserDisplayName(userNodeQuery.data);
    const registeredAt = cropMonitoring.result?.created ? formatDate(new Date(cropMonitoring.result.created)) : null;

    return (
        <RegistryCard>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Stack direction='row' spacing={0.5} alignItems='center'>
                    {fromFieldview && (
                        <Icon type='fieldview' style={{ fill: '#F7B618', width: '20px', height: '20px' }} />
                    )}
                    <Typography variant='h6' color='primary'>
                        {_('Cosecha')}
                    </Typography>
                </Stack>
                {canEdit && (
                    <>
                        <IconButton
                            size='small'
                            aria-label={_('Más opciones')}
                            onClick={(e) => setMenuAnchor(e.currentTarget)}
                        >
                            <MoreVert fontSize='small' />
                        </IconButton>
                        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                            <MenuItem onClick={startEditing} disabled={editing}>
                                {_('Editar')}
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Stack>

            {editing ? (
                <Stack spacing={2}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: 1,
                            overflow: 'auto',
                            width: '100%',
                        }}
                    >
                        <EditCell>
                            <Typography variant='caption' color='primary'>
                                {_('Fecha de cosecha')}
                            </Typography>
                            <MuiDatePicker
                                value={harvestDate}
                                onChange={setHarvestDate}
                                slotProps={{
                                    textField: { size: 'small', placeholder: _('Seleccionar una fecha') },
                                }}
                            />
                        </EditCell>
                        <Cell title={_('Hectáreas cosechadas')} value={harvestedArea} />
                        <EditCell>
                            <Typography variant='caption' color='primary'>
                                {_('Humedad de cosecha')}
                            </Typography>
                            <TextField
                                size='small'
                                type='number'
                                value={humidity ?? ''}
                                onChange={(e) =>
                                    setHumidity(e.target.value === '' ? undefined : Number(e.target.value))
                                }
                                sx={{
                                    background: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        top: '-10px',
                                    },
                                }}
                                InputProps={{ endAdornment: <InputAdornment position='end'>%</InputAdornment> }}
                            />
                        </EditCell>
                        <EditCell>
                            <Typography variant='caption' color='primary'>
                                {_('Rendimiento total')}
                            </Typography>
                            <TextField
                                size='small'
                                type='number'
                                value={
                                    averageYield != null
                                        ? (units.toYield(averageYield, cropMonitoring.crop, 2).value ?? '')
                                        : ''
                                }
                                onChange={(e) =>
                                    setAverageYield(
                                        e.target.value === ''
                                            ? undefined
                                            : units.fromYield(e.target.value, cropMonitoring.crop)
                                    )
                                }
                                sx={{
                                    background: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        top: '-10px',
                                    },
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            {units.getUnit('yield', cropMonitoring.crop)}
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </EditCell>
                    </Box>

                    <EditActions
                        onCancel={cancelEditing}
                        onSave={handleSave}
                        saveDisabled={!isValid || isMutating}
                        cancelDisabled={isMutating}
                        saveColor='secondary'
                        saveStartIcon={isMutating ? <CircularProgress size={18} /> : <Save fontSize='small' />}
                    />
                </Stack>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 1,
                        overflow: 'auto',
                        width: '100%',
                    }}
                >
                    <Cell title={_('Fecha de cosecha')} value={harvestDateDisplay} />
                    <Cell title={_('Hectáreas cosechadas')} value={harvestedArea} />
                    <Cell title={_('Humedad de cosecha')} value={humidityDisplay} />
                    <Cell title={_('Rendimiento total')} value={averageYieldDisplay} highlighted />
                </Box>
            )}

            <RegistryNotes notes={notes} fieldId={cropMonitoring.field} closedCropMonitoringId={cropMonitoring.id} />

            {!editing && (
                <Stack alignItems='flex-end'>
                    <Button variant='contained' color='primary' onClick={handleViewReport}>
                        {_('Ver reporte')}
                    </Button>
                </Stack>
            )}

            {fromFieldview ? (
                <Typography variant='caption' color='primary.mui.inactive'>
                    {_('Sincronizado desde FieldView')}
                </Typography>
            ) : (
                registeredAt && <RegistryFooter authorName={authorName} date={registeredAt} />
            )}
        </RegistryCard>
    );
};

export default RegistryHarvest;
